import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { EnvironmentService, EventService, ListService, fromList } from '@core/services';
import { GridService } from '@core/services';
import { currentTimeInSeconds } from '@util/list';

import { ListOfValuesResponse, MultipleListOfValuesResponse } from '#types/list';

import { AuthActions } from '@auth/state';
import { CONSTANTS } from '@customer/constants';
import { CustomerService } from '@customer/services';

import * as CustomerActions from './customer.actions';

@Injectable()
export class CustomerEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private gridService: GridService,
    private listService: ListService,
    private environmentService: EnvironmentService,
    private customerService: CustomerService,
    private eventService: EventService
  ) {}

  RequestLists$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CustomerActions.RequestLists),
        concatLatestFrom(() => [this.store.select(fromList.selectCustomerLists)]),
        map(([_, lists]) => {
          let lastUpdated = -1;

          for (const list of Object.keys(lists)) {
            // skip parameterized lists
            if (lists[list] && !Array.isArray(lists[list].data)) {
              continue;
            }

            if (lists[list]) {
              lastUpdated = Math.max(lastUpdated, currentTimeInSeconds() - (lists[list].updated ?? 0));
            }
          }

          if (lastUpdated === -1 || lastUpdated > this.environmentService.settings.secondsToCacheLists) {
            this.store.dispatch(CustomerActions.RequestCustomerLists());
          }
        })
      );
    },
    { dispatch: false }
  );

  requestCustomerList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.RequestCustomerLists, AuthActions.LoginSuccess),
      exhaustMap(() => {
        return this.listService.getListOfValues<MultipleListOfValuesResponse>('customer').pipe(
          this.eventService.indicate(CONSTANTS.LIST_ALL),
          map((lists) => CustomerActions.RequestCustomerListsSuccess({ lists })),
          catchError((error) => of(CustomerActions.RequestCustomerListsFailure(error)))
        );
      })
    );
  });

  requestCustomerDictionaryFieldList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.RequestCustomerDictionaryFieldList),
      exhaustMap((parameters) => {
        return this.listService
          .getListOfValuesWithParameters<ListOfValuesResponse>('customer', 'customerDict', { custid: parameters.custid })
          .pipe(
            this.eventService.indicate(CONSTANTS.LIST_CUSTOMER_DICT),
            map((list) => CustomerActions.RequestCustomerDictionaryFieldListSuccess({ custid: parameters.custid, list })),
            catchError((error) => of(CustomerActions.RequestCustomerDictionaryFieldListFailure(error)))
          );
      })
    );
  });

  RequestCustomerItemUomList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.RequestCustomerItemUomList),
      exhaustMap((parameters) => {
        return this.listService
          .getListOfValuesWithParameters<ListOfValuesResponse>('customer', 'customerItemUom', {
            custid: parameters.custid,
            item: parameters.item,
          })
          .pipe(
            this.eventService.indicate(CONSTANTS.LIST_CUSTOMER_ITEM_UOM),
            map((list) => CustomerActions.RequestCustomerItemUomListSuccess({ custid: parameters.custid, item: parameters.item, list })),
            catchError((error) => of(CustomerActions.RequestCustomerItemUomListFailure(error)))
          );
      })
    );
  });

  CustomerLookupRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        CustomerActions.CustomerLookupDialogRequestData,
        CustomerActions.CustomerLookupDialogRequestDataWithFilters,
        CustomerActions.CustomerLookupDialogRequestDataWithParameters
      ),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.customerService.getCustomerLookup({ filters, page, rows, sorts }).pipe(
          map((data) => CustomerActions.CustomerLookupDialogRequestDataSuccess({ id, data })),
          catchError((_error) => of(CustomerActions.CustomerLookupDialogRequestDataFailure({ id })))
        )
      )
    );
  });
}
